import {
  OmsAllocationList,
  OmsApplyLayout,
  OmsApprovedUserList,
  OmsApprovedUserRequest,
  OmsApprovedUserRequestInput,
  OmsAuditCsv,
  OmsAuditList,
  OmsAuditQuery,
  OmsBreachList,
  OmsConfirmRequest,
  OmsFillImportStatus,
  OmsFillList,
  OmsFillQuery,
  OmsIdempotencyHeaders,
  OmsInstrumentList,
  OmsInstrumentPrice,
  OmsInstrumentQuery,
  OmsKillSwitch,
  OmsLayout,
  OmsLayoutList,
  OmsLayoutQuery,
  OmsOpenLotList,
  OmsOrder,
  OmsOrderDraft,
  OmsOrderHistory,
  OmsOrderList,
  OmsOrderModify,
  OmsOrderQuery,
  OmsPositionList,
  OmsPositionQuery,
  OmsPostTradeLimits,
  OmsPreviewQuery,
  OmsPreviewTicketPutHeaders,
  OmsReason,
  OmsReference,
  OmsRejectRequest,
  OmsRule,
  OmsRuleList,
  OmsRuleQuery,
  OmsRuleSaved,
  OmsRuleUpdate,
  OmsSession,
  OmsSharedView,
  OmsSharedViewInput,
  OmsSharedViewList,
  OmsSharedViewQuery,
  OmsTransitionResult,
} from '../interfaces/oms';
import { empty, json, problem, type PathDef, type ResponseDef } from '../../util/pathDef';

function omsRead(success: ResponseDef, notFound = false): PathDef['responses'] {
  const responses: PathDef['responses'] = {
    200: success,
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Not entitled to OMS'),
    502: problem('OMS service unavailable'),
  };
  if (notFound) responses[404] = problem('Not found');
  return responses;
}

function omsWrite(success: ResponseDef, notFound = false): PathDef['responses'] {
  const responses = omsRead(success, notFound);
  responses[409] = problem('Conflict');
  return responses;
}

function omsRemoved(): PathDef['responses'] {
  return {
    204: empty('Deleted'),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Not entitled to OMS'),
    404: problem('Not found'),
    502: problem('OMS service unavailable'),
  };
}

const write = { security: 'bearerAuth' as const, headers: OmsIdempotencyHeaders, tags: ['oms'] };

export const omsSessionGet: PathDef = {
  method: 'get',
  path: '/oms/session',
  handler: 'domains/bff/src/oms/session/get/index.handler',
  tags: ['oms'],
  summary: 'OMS session',
  description: 'Entitlement for this login: OMS flag, read, write guard, book rights and the kill switch. Datasource comes from the JWT.',
  security: 'bearerAuth',
  responses: omsRead(json('OMS session', OmsSession)),
};

export const omsReferenceGet: PathDef = {
  method: 'get',
  path: '/oms/reference',
  handler: 'domains/bff/src/oms/reference/get/index.handler',
  tags: ['oms'],
  summary: 'OMS reference lists',
  description:
    'Dropdown and filter lists. statuses is the GetOrderStatuses dictionary from the order-status Lambda: each item is that Lambda key and value, with no renaming. Display labels come from the shared status map.',
  security: 'bearerAuth',
  responses: omsRead(json('OMS reference lists', OmsReference)),
};

export const omsLayoutsGet: PathDef = {
  method: 'get',
  path: '/oms/layouts',
  handler: 'domains/bff/src/oms/layouts/list/index.handler',
  tags: ['oms'],
  summary: 'List OMS layouts',
  description:
    'The saved column layout for one tab, if the caller has one, with the grouping the user left on that grid (or, on the custom tab, the Custom layout last opened). One layout per user and tab. Not order data.',
  security: 'bearerAuth',
  query: OmsLayoutQuery,
  responses: omsRead(json('OMS layouts', OmsLayoutList)),
};

export const omsLayoutPut: PathDef = {
  method: 'put',
  path: '/oms/layouts/{tab}/{layoutName}',
  handler: 'domains/bff/src/oms/layouts/put/index.handler',
  tags: ['oms'],
  summary: 'Upsert an OMS layout',
  description:
    'Saves the one column layout for a tab: column ids, order, widths, visibility and the default sort, plus the grouping the user left on that grid (monitor: positionGroupBy, blotter: fillGroupBy) or, on the custom tab, the saved Custom layout last opened (customLayoutId, with empty columns). Not an order write, so it is not behind the write guard.',
  security: 'bearerAuth',
  request: OmsLayout,
  responses: omsWrite(json('OMS layout', OmsLayout)),
};

export const omsLayoutDelete: PathDef = {
  method: 'delete',
  path: '/oms/layouts/{tab}/{layoutName}',
  handler: 'domains/bff/src/oms/layouts/delete/index.handler',
  tags: ['oms'],
  summary: 'Delete an OMS layout',
  description: 'Soft-deletes the one column layout for a tab. The item stays with a deleted marker.',
  security: 'bearerAuth',
  responses: omsRemoved(),
};

export const omsSharedViewsGet: PathDef = {
  method: 'get',
  path: '/oms/shared-views',
  handler: 'domains/bff/src/oms/sharedViews/list/index.handler',
  tags: ['oms'],
  summary: 'List saved Custom layouts',
  description: "Everyone in the caller's company sees the same list. createdBy is the first writer's name. Not order data.",
  security: 'bearerAuth',
  query: OmsSharedViewQuery,
  responses: omsRead(json('Saved Custom layouts', OmsSharedViewList)),
};

export const omsSharedViewPut: PathDef = {
  method: 'put',
  path: '/oms/shared-views/{kind}/{viewId}',
  handler: 'domains/bff/src/oms/sharedViews/put/index.handler',
  tags: ['oms'],
  summary: 'Upsert a saved Custom layout',
  description:
    'viewId is a UUID the client picks for a new view. Anyone in the same company can update it; the first writer stays its owner. Holds a name and panel ids, widths and filters. Not an order write, so it is not behind the write guard.',
  security: 'bearerAuth',
  request: OmsSharedViewInput,
  responses: { ...omsRead(json('Saved Custom layout updated', OmsSharedView), true), 201: json('Saved Custom layout created', OmsSharedView) },
};

export const omsSharedViewDelete: PathDef = {
  method: 'delete',
  path: '/oms/shared-views/{kind}/{viewId}',
  handler: 'domains/bff/src/oms/sharedViews/delete/index.handler',
  tags: ['oms'],
  summary: 'Delete a saved Custom layout',
  description: 'Anyone in the same company can delete it. The item stays with a deleted marker.',
  security: 'bearerAuth',
  responses: omsRemoved(),
};

export const omsPreviewTicketsGet: PathDef = {
  method: 'get',
  path: '/oms/preview-tickets',
  handler: 'domains/bff/src/oms/previewTickets/list/index.handler',
  tags: ['oms'],
  summary: 'List Order Preview tickets',
  description:
    'Order Preview: orders in the live window (today and tomorrow) whose on-prem status is Unsubmitted, from the order-read cache. ' +
    'Each ticket carries its allocation instructions and the check saved with it (RedFlagsResult); NOT_CHECKED when on-prem has no evaluation. ' +
    'Rule hits are not saved on-prem for a staged ticket. Book filtering by permission waits on the approved list.',
  security: 'bearerAuth',
  query: OmsPreviewQuery,
  responses: omsRead(json('Preview tickets', OmsOrderList)),
};

export const omsOrdersGet: PathDef = {
  method: 'get',
  path: '/oms/orders',
  handler: 'domains/bff/src/oms/orders/list/index.handler',
  tags: ['oms'],
  summary: 'List orders',
  description:
    'Order blotter for the signed-in datasource. The BFF asks the order-read Lambda for today and tomorrow. The row supplies orderId, books, status, qty, filledQty, tifDate, side, bbTicker, instrumentDescription, exchange, product, contractMonthCode, contractYear and isSpread when the cache has them. Allocations and fills are included when that same read recorded them. Query filters are accepted and not applied.',
  security: 'bearerAuth',
  query: OmsOrderQuery,
  responses: omsRead(json('Orders', OmsOrderList)),
};

export const omsOrderGet: PathDef = {
  method: 'get',
  path: '/oms/orders/{orderId}',
  handler: 'domains/bff/src/oms/orders/get/index.handler',
  tags: ['oms'],
  summary: 'Get one order',
  description: 'One order from the order-read cache, including allocations and fills when the cache has them.',
  security: 'bearerAuth',
  responses: omsRead(json('Order', OmsOrder), true),
};

export const omsOrderAllocationsGet: PathDef = {
  method: 'get',
  path: '/oms/orders/{orderId}/allocations',
  handler: 'domains/bff/src/oms/orders/allocations/list/index.handler',
  tags: ['oms'],
  summary: 'List order allocations',
  description: 'Allocation instructions for one order. Fills, when present, come from the same order-read cache.',
  security: 'bearerAuth',
  responses: omsRead(json('Allocations', OmsAllocationList), true),
};

export const omsOrderHistoryGet: PathDef = {
  method: 'get',
  path: '/oms/orders/{orderId}/history',
  handler: 'domains/bff/src/oms/orders/history/get/index.handler',
  tags: ['oms'],
  summary: 'Order history',
  description: 'On-prem history text for one order, unchanged. Cloud audit entries are a later story.',
  security: 'bearerAuth',
  responses: omsRead(json('Order history', OmsOrderHistory), true),
};

export const omsInstrumentsGet: PathDef = {
  method: 'get',
  path: '/oms/instruments',
  handler: 'domains/bff/src/oms/instruments/list/index.handler',
  tags: ['oms'],
  summary: 'List tradeable instruments',
  description: 'Cascading Market Library options for Create Order.',
  security: 'bearerAuth',
  query: OmsInstrumentQuery,
  responses: omsRead(json('Instruments', OmsInstrumentList)),
};

export const omsInstrumentPriceGet: PathDef = {
  method: 'get',
  path: '/oms/instruments/{instrumentId}/price',
  handler: 'domains/bff/src/oms/instruments/price/get/index.handler',
  tags: ['oms'],
  summary: 'Latest close',
  description: 'Latest close and its date for one instrument.',
  security: 'bearerAuth',
  responses: omsRead(json('Latest close', OmsInstrumentPrice), true),
};

export const omsPositionsGet: PathDef = {
  method: 'get',
  path: '/oms/positions',
  handler: 'domains/bff/src/oms/positions/list/index.handler',
  tags: ['oms'],
  summary: 'List positions',
  description: 'Positions by total, tier or account.',
  security: 'bearerAuth',
  query: OmsPositionQuery,
  responses: omsRead(json('Positions', OmsPositionList)),
};

export const omsPositionOpenLotsGet: PathDef = {
  method: 'get',
  path: '/oms/positions/{positionKey}/open-lots',
  handler: 'domains/bff/src/oms/positions/openLots/list/index.handler',
  tags: ['oms'],
  summary: 'List open lots',
  description: 'Open lots for one position. Delivery phase 2.',
  security: 'bearerAuth',
  responses: omsRead(json('Open lots', OmsOpenLotList), true),
};

export const omsRulesGet: PathDef = {
  method: 'get',
  path: '/oms/rules',
  handler: 'domains/bff/src/oms/rules/list/index.handler',
  tags: ['oms'],
  summary: 'List OMS rules',
  description: 'Pre-trade and post-trade checks. Read-only until rule settings exist.',
  security: 'bearerAuth',
  query: OmsRuleQuery,
  responses: omsRead(json('Rules', OmsRuleList)),
};

export const omsRuleGet: PathDef = {
  method: 'get',
  path: '/oms/rules/{ruleId}',
  handler: 'domains/bff/src/oms/rules/get/index.handler',
  tags: ['oms'],
  summary: 'Get one OMS rule',
  security: 'bearerAuth',
  responses: omsRead(json('Rule', OmsRule), true),
};

export const omsPostTradeLimitsGet: PathDef = {
  method: 'get',
  path: '/oms/post-trade/limits',
  handler: 'domains/bff/src/oms/postTrade/limits/get/index.handler',
  tags: ['oms'],
  summary: 'Post-trade limits',
  description: 'Soft and hard limits and the on-prem spread net, by position key. Empty until on-prem returns limit layouts.',
  security: 'bearerAuth',
  responses: omsRead(json('Post-trade limits', OmsPostTradeLimits)),
};

export const omsFillsGet: PathDef = {
  method: 'get',
  path: '/oms/fills',
  handler: 'domains/bff/src/oms/fills/list/index.handler',
  tags: ['oms'],
  summary: 'List fills',
  description:
    'Trade blotter. Fills from the order-read cache attached to OMS orders, for the current trade day, newest first, 50 per page. allocation, book, exchange, product, side and q are applied on the server before paging. Bought, sold and no-side quantities are separate. A market value total is sent only when every fill has one and they share a currency. Facets list books, exchanges and products for the whole trade day. Clearing account is null when the fill has none. A fill whose order has no side is included with side omitted. Currency, market value and strategy stay absent until a later read has them. Importer status is not read. Column sort is applied in the browser to the current page.',
  security: 'bearerAuth',
  query: OmsFillQuery,
  responses: omsRead(json('Fills', OmsFillList)),
};

export const omsApprovedUsersGet: PathDef = {
  method: 'get',
  path: '/oms/approved-users',
  handler: 'domains/bff/src/oms/approvedUsers/list/index.handler',
  tags: ['oms'],
  summary: 'List approved OMS users',
  description: 'Cloud approved-order list. Not an on-prem call.',
  security: 'bearerAuth',
  responses: omsRead(json('Approved users', OmsApprovedUserList)),
};

export const omsApprovedUserRequestsPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/approvedUsers/requests/post/index.handler',
  method: 'post',
  path: '/oms/approved-users/requests',
  summary: 'Request an approved-user change',
  description: 'Opens a change request. A different person approves it.',
  request: OmsApprovedUserRequestInput,
  responses: omsWrite(json('Approved-user request', OmsApprovedUserRequest)),
};

export const omsApprovedUserRequestApprovePost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/approvedUsers/requests/approve/index.handler',
  method: 'post',
  path: '/oms/approved-users/requests/{requestId}/approve',
  summary: 'Approve an approved-user request',
  description: 'Approver must differ from the requester.',
  responses: omsWrite(json('Approved-user request', OmsApprovedUserRequest), true),
};

export const omsApprovedUserRequestRejectPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/approvedUsers/requests/reject/index.handler',
  method: 'post',
  path: '/oms/approved-users/requests/{requestId}/reject',
  summary: 'Reject an approved-user request',
  request: OmsReason,
  responses: omsWrite(json('Approved-user request', OmsApprovedUserRequest), true),
};

export const omsKillSwitchPut: PathDef = {
  method: 'put',
  path: '/oms/kill-switch',
  handler: 'domains/bff/src/oms/killSwitch/put/index.handler',
  tags: ['oms'],
  summary: 'Set the OMS kill switch',
  description:
    'Turns the Cloud kill switch on or off for this datasource or its books. Needs the admin right, not the write guard, so it can always be turned on.',
  security: 'bearerAuth',
  request: OmsKillSwitch,
  responses: omsWrite(json('Kill switch', OmsKillSwitch)),
};

export const omsPreviewTicketsPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/previewTickets/post/index.handler',
  method: 'post',
  path: '/oms/preview-tickets',
  summary: 'Send a ticket to Order Preview',
  description: 'Stages a draft. Does not send the order to a venue.',
  request: OmsOrderDraft,
  responses: omsWrite(json('Preview ticket', OmsOrder)),
};

export const omsPreviewTicketPut: PathDef = {
  method: 'put',
  path: '/oms/preview-tickets/{orderId}',
  handler: 'domains/bff/src/oms/previewTickets/put/index.handler',
  tags: ['oms'],
  summary: 'Edit a preview ticket',
  description: 'Replaces a preview ticket. If-Match is the ticket lastModifiedOn.',
  security: 'bearerAuth',
  headers: OmsPreviewTicketPutHeaders,
  request: OmsOrderDraft,
  responses: omsWrite(json('Preview ticket', OmsOrder), true),
};

export const omsPreviewTicketEvaluatePost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/previewTickets/evaluate/index.handler',
  method: 'post',
  path: '/oms/preview-tickets/{orderId}/evaluate',
  summary: 'Run PreTrade checks',
  description: 'Runs the checks and saves nothing.',
  responses: omsWrite(json('Preview ticket', OmsOrder), true),
};

export const omsPreviewTicketsConfirmPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/previewTickets/confirm/index.handler',
  method: 'post',
  path: '/oms/preview-tickets/confirm',
  summary: 'Confirm preview tickets',
  description: 'Bulk confirm. Warn hits need an acknowledgement. One result per ticket.',
  request: OmsConfirmRequest,
  responses: omsWrite(json('Confirm results', OmsTransitionResult)),
};

export const omsPreviewTicketsRejectPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/previewTickets/reject/index.handler',
  method: 'post',
  path: '/oms/preview-tickets/reject',
  summary: 'Reject preview tickets',
  description: 'Bulk reject with a reason. One result per ticket.',
  request: OmsRejectRequest,
  responses: omsWrite(json('Reject results', OmsTransitionResult)),
};

export const omsAuditGet: PathDef = {
  method: 'get',
  path: '/oms/audit',
  handler: 'domains/bff/src/oms/audit/list/index.handler',
  tags: ['oms'],
  summary: 'List OMS audit entries',
  description: 'Cloud audit of OMS actions. On-prem audit is a later phase.',
  security: 'bearerAuth',
  query: OmsAuditQuery,
  responses: omsRead(json('Audit entries', OmsAuditList)),
};

export const omsAuditExportGet: PathDef = {
  method: 'get',
  path: '/oms/audit/export',
  handler: 'domains/bff/src/oms/audit/export/index.handler',
  tags: ['oms'],
  summary: 'Export the OMS audit',
  description: 'CSV of the same audit query for compliance users.',
  security: 'bearerAuth',
  query: OmsAuditQuery,
  responses: omsRead({ description: 'Audit CSV', schema: OmsAuditCsv, contentType: 'text/csv' }),
};

export const omsRulePut: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/rules/put/index.handler',
  method: 'put',
  path: '/oms/rules/{ruleId}',
  summary: 'Update an OMS rule',
  description:
    'Turns a rule on or off and replaces the caps that rule reads, in one write. Caps are stored by name, so the PreTrade and Post Trade restricted lists share their symbols and products, and the two notice-month rules share their first notice dates: saving either rule replaces both. A save that matches what is stored writes nothing, so a replay is harmless. 409 when another save changed the rule first.',
  request: OmsRuleUpdate,
  responses: omsWrite(json('Saved rule', OmsRuleSaved), true),
};

export const omsPreviewTicketWaivePost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/previewTickets/waive/index.handler',
  method: 'post',
  path: '/oms/preview-tickets/{orderId}/waive',
  summary: 'Waive a blocked ticket',
  request: OmsReason,
  responses: omsWrite(json('Preview ticket', OmsOrder), true),
};

export const omsPostTradeApplyLayoutPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/postTrade/applyLayout/index.handler',
  method: 'post',
  path: '/oms/post-trade/apply-layout',
  summary: 'Apply a post-trade layout',
  description: 'Delivery phase 2.',
  request: OmsApplyLayout,
  responses: omsWrite(json('Post-trade limits', OmsPostTradeLimits)),
};

export const omsPostTradeBreachesGet: PathDef = {
  method: 'get',
  path: '/oms/post-trade/breaches',
  handler: 'domains/bff/src/oms/postTrade/breaches/list/index.handler',
  tags: ['oms'],
  summary: 'List post-trade breaches',
  description: 'Delivery phase 2.',
  security: 'bearerAuth',
  responses: omsRead(json('Breaches', OmsBreachList)),
};

export const omsPostTradeBreachReviewPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/postTrade/breaches/review/index.handler',
  method: 'post',
  path: '/oms/post-trade/breaches/{breachId}/review',
  summary: 'Review a post-trade breach',
  description: 'Delivery phase 2.',
  request: OmsReason,
  responses: omsWrite(json('Breaches', OmsBreachList), true),
};

export const omsPositionFlattenPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/positions/flatten/index.handler',
  method: 'post',
  path: '/oms/positions/{positionKey}/flatten',
  summary: 'Stage a flatten ticket',
  description: 'Stages a ticket in Order Preview. Does not send it to a venue.',
  responses: omsWrite(json('Preview ticket', OmsOrder), true),
};

export const omsPositionRollPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/positions/roll/index.handler',
  method: 'post',
  path: '/oms/positions/{positionKey}/roll',
  summary: 'Stage a roll ticket',
  description: 'Stages a roll ticket in Order Preview. Does not send it to a venue.',
  responses: omsWrite(json('Preview ticket', OmsOrder), true),
};

export const omsOrderPausePost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/orders/pause/index.handler',
  method: 'post',
  path: '/oms/orders/{orderId}/pause',
  summary: 'Pause an order',
  description: 'Delivery phase 2.',
  responses: omsWrite(json('Order', OmsOrder), true),
};

export const omsOrderResumePost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/orders/resume/index.handler',
  method: 'post',
  path: '/oms/orders/{orderId}/resume',
  summary: 'Resume an order',
  description: 'Delivery phase 2.',
  responses: omsWrite(json('Order', OmsOrder), true),
};

export const omsOrderModifyPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/orders/modify/index.handler',
  method: 'post',
  path: '/oms/orders/{orderId}/modify',
  summary: 'Modify an order',
  description: 'Sends a replace. Delivery phase 2.',
  request: OmsOrderModify,
  responses: omsWrite(json('Order', OmsOrder), true),
};

export const omsOrderCancelPost: PathDef = {
  ...write,
  handler: 'domains/bff/src/oms/orders/cancel/index.handler',
  method: 'post',
  path: '/oms/orders/{orderId}/cancel',
  summary: 'Cancel an order',
  description: 'Delivery phase 2.',
  responses: omsWrite(json('Order', OmsOrder), true),
};

export const omsFillsImportStatusGet: PathDef = {
  method: 'get',
  path: '/oms/fills/import-status',
  handler: 'domains/bff/src/oms/fills/importStatus/get/index.handler',
  tags: ['oms'],
  summary: 'Fill import status',
  description: 'Whether fills are still waiting on the Trades Importer. Delivery phase 2.',
  security: 'bearerAuth',
  responses: omsRead(json('Fill import status', OmsFillImportStatus)),
};

export const omsPaths: PathDef[] = [
  omsSessionGet,
  omsReferenceGet,
  omsLayoutsGet,
  omsLayoutPut,
  omsLayoutDelete,
  omsSharedViewsGet,
  omsSharedViewPut,
  omsSharedViewDelete,
  omsPreviewTicketsGet,
  omsOrdersGet,
  omsOrderGet,
  omsOrderAllocationsGet,
  omsOrderHistoryGet,
  omsInstrumentsGet,
  omsInstrumentPriceGet,
  omsPositionsGet,
  omsPositionOpenLotsGet,
  omsRulesGet,
  omsRuleGet,
  omsPostTradeLimitsGet,
  omsFillsGet,
  omsApprovedUsersGet,
  omsApprovedUserRequestsPost,
  omsApprovedUserRequestApprovePost,
  omsApprovedUserRequestRejectPost,
  omsKillSwitchPut,
  omsPreviewTicketsPost,
  omsPreviewTicketPut,
  omsPreviewTicketEvaluatePost,
  omsPreviewTicketsConfirmPost,
  omsPreviewTicketsRejectPost,
  omsAuditGet,
  omsAuditExportGet,
  omsRulePut,
  omsPreviewTicketWaivePost,
  omsPostTradeApplyLayoutPost,
  omsPostTradeBreachesGet,
  omsPostTradeBreachReviewPost,
  omsPositionFlattenPost,
  omsPositionRollPost,
  omsOrderPausePost,
  omsOrderResumePost,
  omsOrderModifyPost,
  omsOrderCancelPost,
  omsFillsImportStatusGet,
];
